import { EventIvStatusUpdated } from 'apps/wrapper/src/types/event-bus';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useEventBus } from '../../../../../hooks/use-event-bus';
import { useInsuranceApi } from '../../../../../hooks/use-insurance-api';
import { ContactApi } from '../../../../../services/api-client/contact-api';
import CarrierSummary from '../../../../../services/insurance/types/carrier-name';
import { IvSummaryStatus } from '../../../../../services/insurance/types/iv-summary-status';
import { Provider } from '../../../../../services/insurance/types/provider';
import { VerificationSummary } from '../../../../../services/insurance/types/verification-summary';
import { IvPatientInfo } from '../../types/iv-patient-info';
import { PatientIvFlowProps } from '../../types/patient-iv-flow-props';
import { PatientIvStatusDialogContext, PatientIvStatusDialogContextProps } from './context';

export interface PatientIvStatusDialogProviderProps {
  opened: boolean;
  onClose: () => void;
  locationId: number;
  patientFlow: PatientIvFlowProps;
  providerId: number | undefined;
  selectedIvSummary?: VerificationSummary;
  appointment?: {
    isEnded?: boolean;
  };
  children?: React.ReactNode;
}

export const PatientIvStatusDialogProvider: FC<PatientIvStatusDialogProviderProps> = (props) => {
  const { opened, onClose, patientFlow, locationId, providerId, selectedIvSummary, appointment } = props;
  const { publish, publishToIframes } = useEventBus();
  const { getProviders, getCarrierSummaries } = useInsuranceApi();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isReady, setIsReady] = useState<boolean>(false);
  const [patientInfo, setPatientInfo] = useState<IvPatientInfo>();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [carrierSummaries, setCarrierSummaries] = useState<CarrierSummary[]>([]);
  const contactApi = useMemo(() => ContactApi.getInstance(), [ContactApi]);

  useEffect(() => {
    if (!patientFlow) return;

    const initPatientFlow = async () => {
      setIsLoading(true);

      const [pInfo, prov, carriers] = await Promise.all([
        contactApi.getPatientInfoById(patientFlow.patientId),
        getProviders(locationId),
        getCarrierSummaries(locationId)
      ]);

      setPatientInfo(pInfo);
      setProviders(prov);
      setCarrierSummaries(carriers);
      setIsLoading(false);
      setIsReady(true);
    };

    initPatientFlow();
  }, [patientFlow, locationId]);

  const getProvider = useCallback(
    (id?: number) => {
      if (!id) return null;
      return providers.find((p) => p.id === id);
    },
    [providers]
  );

  const getCarrierSummary = useCallback(
    (carrierNameId?: number) => {
      if (!carrierNameId) return null;
      return carrierSummaries.find((c) => c.idCarrierName === carrierNameId);
    },
    [carrierSummaries]
  );

  const sendIvStatusUpdatedEvent = useCallback(
    (status?: IvSummaryStatus) => {
      const event = {
        messageType: 'ivStatusUpdated',
        data: {
          patientId: patientFlow.patientId,
          priority: patientFlow.verificationPriority,
          providerId,
          status
        }
      } as EventIvStatusUpdated;

      publish(event);
      publishToIframes(event);
    },
    [patientFlow, selectedIvSummary, providerId]
  );

  const contextValue = {
    opened,
    onClose,
    isLoading,
    isReady,
    locationId,
    patientFlow,
    providerId: providerId || selectedIvSummary?.idProvider,
    setIsLoading,
    patientInfo,
    selectedIvSummary,
    providers,
    getProvider,
    getCarrierSummary,
    appointment,
    sendIvStatusUpdatedEvent
  } as PatientIvStatusDialogContextProps;

  return (
    <PatientIvStatusDialogContext.Provider value={contextValue}>{props.children}</PatientIvStatusDialogContext.Provider>
  );
};
